import React, { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { Edit2, Repeat, Timer1 } from 'iconsax-react';
import { useErrorStore } from '@/stores/profile/errorStore';
import { useToast } from '@/hooks/use-toast';
import LoaderSpin from '@/components/LoaderSpin';
import {
    resendOtpHandler,
    verifyOtp,
    convertToEnglishDigits,
} from '@/components/(dashboard)/login-register/Utils/otp';
import { useWebOtp } from '@/components/(dashboard)/login-register/Utils/useWebOtp';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

interface OtpInputProps {
    phone: string;
    onVerify: (otp: string) => Promise<{ token: string; success: boolean; message: string }>;
    onEditPhone: () => void;
    loading: boolean;
    onSuccess: () => void;
    popup?: boolean;
    retryTime: number;
    onRetry?: () => void;
    selectedWizard?: boolean;
    otpBtnText?: string | undefined;
    newStyle?: boolean;
    popupWizardStyle?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
    onVerify,
    onEditPhone,
    loading,
    onSuccess,
    retryTime,
    phone,
    onRetry,
    selectedWizard,
    otpBtnText,
    newStyle,
    popupWizardStyle,
}) => {
    const [otp, setOtp] = useState('');
    const hasSubmittedRef = useRef(false);
    const { setError } = useErrorStore();
    const { toast } = useToast();

    const debouncedVerifyOtp = useRef(
        debounce((otpCode: string) => {
            verifyOtp({
                otp: otpCode,
                onVerify,
                onSuccess,
                setError,
                toast,
                inputRefs: [],
                resetSubmitted: () => {
                    hasSubmittedRef.current = false;
                },
            });
        }, 500),
    ).current;

    const handleManualSubmit = () => {
        if (otp.length === 4 && !hasSubmittedRef.current) {
            hasSubmittedRef.current = true;
            debouncedVerifyOtp(otp);
        }
    };

    const handleResendOtp = () => {
        resendOtpHandler(retryTime, setError, onRetry, () => {
            setOtp('');
            hasSubmittedRef.current = false;
        });
    };

    useWebOtp((code) => {
        const normalized = convertToEnglishDigits(code).replace(/\D/g, '').slice(0, 4);
        setOtp(normalized);
        hasSubmittedRef.current = false;
    });

    useEffect(() => {
        return () => {
            debouncedVerifyOtp.cancel();
            hasSubmittedRef.current = false;
        };
    }, [debouncedVerifyOtp]);

    if (popupWizardStyle) {
        return (
            <div className='w-full flex flex-col items-center relative'>
                {loading && <LoaderSpin newStyle={true} selectedWizard={true} />}

                {/* Sent info */}
                <div className='flex items-center justify-center gap-4 text-[10px] md:text-xs mb-4 w-full select-none text-gray-500 dark:text-gray-400'>
                    <div className='flex items-center gap-1 text-green-600 dark:text-green-400'>
                        <span className='size-4 rounded-full border border-green-600 dark:border-green-400 flex items-center justify-center text-[10px]'>
                            ✓
                        </span>
                        <span>کد تأیید به {phone} ارسال شد</span>
                    </div>
                    <button
                        onClick={onEditPhone}
                        className='flex items-center gap-1 hover:text-gray-700 dark:hover:text-white transition-all font-semibold'
                    >
                        <span>✏️</span>
                        <span>ویرایش شماره</span>
                    </button>
                </div>

                {/* Unified box */}
                <div
                    className={`w-full flex items-center justify-between gap-1 sm:gap-2 border border-[#EEAC43] focus-within:ring-2 focus-within:ring-[#EEAC43]/20 rounded-2xl p-1 bg-white dark:bg-primary-dark transition-all ${loading ? 'opacity-40' : ''}`}
                >
                    <div dir='ltr' className='flex-1 flex justify-center min-w-0'>
                        <InputOTP
                            maxLength={4}
                            value={otp}
                            onChange={(val: string) =>
                                setOtp(convertToEnglishDigits(val).replace(/\D/g, '').slice(0, 4))
                            }
                            onComplete={(val: string) => {
                                const normalized = convertToEnglishDigits(val)
                                    .replace(/\D/g, '')
                                    .slice(0, 4);
                                if (normalized.length === 4 && !hasSubmittedRef.current) {
                                    hasSubmittedRef.current = true;
                                    debouncedVerifyOtp(normalized);
                                }
                            }}
                            autoFocus
                        >
                            <InputOTPGroup className='gap-1 sm:gap-1.5 justify-center'>
                                {[...Array(4)].map((_, index) => (
                                    <InputOTPSlot
                                        key={index}
                                        className='bg-transparent dark:bg-transparent border border-gray-200 dark:border-white/20 rounded-lg size-8 sm:size-9 md:size-10 text-gray-900 dark:text-white text-base sm:text-lg font-bold text-center'
                                        index={index}
                                    />
                                ))}
                            </InputOTPGroup>
                        </InputOTP>
                    </div>

                    <button
                        onClick={handleManualSubmit}
                        className={`shrink-0 bg-gradient-to-l from-[#C07808] to-[#EEAC43] hover:brightness-105 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all ${loading || otp.length !== 4 ? 'opacity-50' : ''}`}
                        disabled={loading || otp.length !== 4}
                    >
                        {loading ? 'در حال بررسی' : otpBtnText || 'تأیید'}
                    </button>
                </div>

                {/* Countdown / Resend */}
                <div className='mt-3 flex flex-col items-center w-full'>
                    {retryTime > 0 ? (
                        <div className='text-[10px] md:text-xs text-gray-500 dark:text-gray-400 flex gap-1 items-center justify-center'>
                            <span>دریافت مجدد کد در</span>
                            <span className='font-bold'>{retryTime} ثانیه</span>
                        </div>
                    ) : (
                        <button
                            onClick={handleResendOtp}
                            className='text-xs font-semibold text-[#EEAC43] hover:underline'
                        >
                            دریافت مجدد کد
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-col items-start justify-center w-full mx-auto relative'>
            {loading && <LoaderSpin newStyle={newStyle} selectedWizard={selectedWizard} />}
            <div className='inline-flex flex-col w-full gap-2 justify-between items-center my-4'>
                <button
                    onClick={onEditPhone}
                    className='flex justify-between hover:brightness-125 text-white/80 rounded-xl items-center text-xs gap-x-2'
                >
                    <span
                        className={`${selectedWizard ? 'text-white' : 'text-black/80 dark:text-white/80'} pt-1`}
                    >
                        شما با شماره {phone} در حال ثبت نام هستید!
                    </span>
                    <Edit2
                        size={15}
                        className={`${selectedWizard ? 'stroke-white' : 'stroke-primary/80 dark:stroke-white '}`}
                    />
                </button>
            </div>

            <div dir='ltr' className={`w-full relative ${loading ? 'opacity-40' : ''}`}>
                <InputOTP
                    maxLength={4}
                    value={otp}
                    onChange={(val: string) =>
                        setOtp(convertToEnglishDigits(val).replace(/\D/g, '').slice(0, 4))
                    }
                    onComplete={(val: string) => {
                        const normalized = convertToEnglishDigits(val)
                            .replace(/\D/g, '')
                            .slice(0, 4);
                        if (normalized.length === 4 && !hasSubmittedRef.current) {
                            hasSubmittedRef.current = true;
                            debouncedVerifyOtp(normalized);
                        }
                    }}
                    autoFocus
                >
                    <InputOTPGroup className='gap-3 w-full justify-center'>
                        {[...Array(4)].map((_, index) => (
                            <InputOTPSlot
                                key={index}
                                className='bg-gray-50 !border aspect-square !rounded-xl size-14 text-primary-dark text-2xl'
                                index={index}
                            />
                        ))}
                    </InputOTPGroup>
                </InputOTP>
                <div className={'mt-3 flex flex-col items-center'}>
                    {retryTime > 0 ? (
                        <div
                            className={`text-sm ${selectedWizard ? 'text-white' : 'text-primary/80 dark:text-white/80'} w-full justify-center flex gap-1 items-center rounded-xl py-2`}
                        >
                            <span className={'text-xs opacity-80'}>
                                ثانیه مانده تا دریافت مجدد کد
                            </span>
                            <span className='w-5 text-base flex justify-center font-bold'>
                                {retryTime}
                            </span>
                            <Timer1
                                size={18}
                                className={`w-5 animate-bounce ${selectedWizard ? 'stroke-white' : 'stroke-primary/80 dark:stroke-white/80'}`}
                            />
                        </div>
                    ) : (
                        <button
                            className={`text-sm flex w-full items-center gap-2 justify-center rounded-lg py-1.5 ${selectedWizard ? 'text-white' : 'dark:text-white text-primary/80'}`}
                            onClick={handleResendOtp}
                        >
                            دریافت مجدد کد
                            <Repeat
                                size={18}
                                className={` ${selectedWizard ? 'fill-white' : 'fill-black dark:fill-white'}`}
                                variant='Bulk'
                            />
                        </button>
                    )}
                </div>
            </div>

            <button
                onClick={handleManualSubmit}
                className={`${newStyle ? 'bg-[#EEAC43]' : 'bg-progress'} mt-4 p-5 w-full text-white rounded-2xl ${loading || otp.length !== 4 ? 'opacity-50' : ''}`}
                disabled={loading || otp.length !== 4}
            >
                {loading ? 'در حال ورود' : otpBtnText || 'ورود'}
            </button>
        </div>
    );
};

export default OtpInput;
